// static/js/cms/area-envelope-images.ts
//
// Seasonal envelope image slots (spring/summer/autumn/winter) on the area
// CMS page.
//
// T1.4 moved hero and intro OFF this file's chokepoint onto a per-site
// content overlay — cms_area_envelope_images.templ now constructs a PLAIN
// cmsImagePicker instance for those two slots (field
// "hero_image_ids"/"image_id", target "area:<slug>:hero"/"area:<slug>:intro")
// whose cms-image-changed emit bubbles straight to editor.ts's generic
// "area:<slug>:<section>" autosave route, exactly like CMSWidgetImage — no
// wrapper needed, so hero/intro no longer touch this file at all.
//
// T3.4b/T3.4c: the four seasonal slots keep this dedicated wrapper, but it no
// longer POSTs to an org-write DB endpoint — seasonal.blocks[] is an
// index-ordered array with no season-keyed map, so the write must
// read-modify-write the FULL array by season, addressed by
// WebsiteContentDraftService.SetSeasonalBlockImage
// (POST …/api/content/set-seasonal-image). That's a shape the generic
// per-field "area:<slug>:<section>" autosave route can't express, so
// seasonal keeps wrapping the SAME cmsImagePicker component every other CMS
// image field uses (image-picker.ts) with its own write chokepoint —
// intercepting the picker's own cms-image-changed emit instead of letting it
// bubble to editor.ts's generic autosave queue — the same divergence as
// poi-board.ts's setImage (T2.9), just onto a draft-overlay endpoint now
// instead of an org-write one.
//
// Unlike poi-board.ts's shared per-POI picker (ONE instance reused across N
// cards, force-remounted on every open to dodge stale state AND to reset the
// picker's _saving gate — see poi-board.ts's openImage doc comment), each
// envelope slot gets its OWN, permanently-mounted cmsImagePicker instance:
// hero, intro, and the four seasons are few and static per section, so
// there's no "N rows sharing one picker" problem. The _saving gate is
// instead released directly on `this` in _writeSlot's `finally` — normally
// editor.ts's real autosave flush ack does that (matched via a DOM event);
// since we never route through it, we clear the flag ourselves so "remove
// from pool" doesn't stay disabled after the first save.
import { cmsImagePicker } from "./image-picker";
import { dispatchToast, getCSRFToken, processResponseTriggers } from "../shared/htmx-helpers";

interface PoolImage {
  id: string;
  url?: string;
  area_slug?: string;
  [key: string]: unknown;
}

export interface CMSAreaEnvelopeImageSlotConfig {
  // slot is the season: "spring" | "summer" | "autumn" | "winter" — the
  // setSeasonalImageRequest.Season value _writeSlot sends.
  slot: string;
  multi: boolean;
  selected: string[];
  pool: PoolImage[];
  min: number;
  max: number;
  // baseURL is the website-scoped editor base
  // (`/organizations/{orgSlug}/websites/{websiteID}`) — same base
  // editor.ts's own draft-overlay writes use. _writeSlot appends
  // "/api/content/set-seasonal-image" to it.
  baseURL: string;
  // areaSlug doubles as the picker's pool filter AND the
  // setSeasonalImageRequest.Slug value (the area content file's slug) —
  // area content lives at content/areas/{locale}/{areaSlug}.json.
  areaSlug: string;
  // defaultScope (3c) puts the wrapped cmsImagePicker into per-entity
  // scope-dropdown mode, opened on this area ("area:<areaSlug>"). Forwarded
  // verbatim to cmsImagePicker below. Optional — cmsImagePicker falls back to
  // "area:"+areaSlug when absent, so omitting it is byte-compatible.
  defaultScope?: string;
  // locale is the content locale being edited — setSeasonalImageRequest.Locale.
  locale: string;
  // Generic fallback toast for a failed write (network error, or a non-2xx
  // response with no JSON `message`) — mirrors poi-board.ts's
  // CMSPOIBoardConfig.actionFailedMsg.
  actionFailedMsg: string;
}

export function cmsAreaEnvelopeImageSlot(config: CMSAreaEnvelopeImageSlotConfig) {
  const picker = cmsImagePicker({
    field: `envelope:${config.slot}`,
    target: `envelope:${config.slot}`,
    multi: config.multi,
    slug: "",
    selected: config.selected,
    pool: config.pool,
    min: config.min,
    max: config.max,
    baseURL: config.baseURL,
    areaSlug: config.areaSlug,
    defaultScope: config.defaultScope,
  });

  return {
    ...picker,

    // onSlotChanged intercepts the picker's own cms-image-changed emit — the
    // templ's @cms-image-changed handler, on the SAME element as x-data, so
    // this target-phase listener fires before the event can bubble to
    // editor.ts's generic autosave queue. MUST stop it: the seasonal write
    // has its own request shape (season-keyed, single image_id) that the
    // generic per-field autosave route can't express — see this file's doc
    // comment.
    onSlotChanged(e: Event) {
      e.stopPropagation();
      const ce = e as CustomEvent<{ value?: unknown }>;
      const value = ce.detail?.value;
      const imageIds = Array.isArray(value) ? value : value ? [String(value)] : [];
      void this._writeSlot(imageIds);
    },

    // _writeSlot is the seasonal write chokepoint (mirrors poi-board.ts's
    // _writeFact): CSRF header, error-toast fallback, HX-Trigger re-dispatch.
    // POSTs to the draft-overlay season-keyed endpoint
    // (WebsiteContentDraftService.SetSeasonalBlockImage, T3.4b/T3.4c) with
    // the EXACT setSeasonalImageRequest shape (internal/api/
    // handler_website_content_draft.go): {locale, slug, season, image_id}.
    // Seasonal slots are single-image (Multi:false, Max:1), so only the
    // first id in the picker's selection is sent — an empty imageIds array
    // (a cleared slot) sends image_id:"", which SetSeasonalBlockImage
    // handles as "clear this season's image" (no-op if the season has no
    // block yet). The picker's own `committed` array already reflects the
    // new selection optimistically (cmsImagePicker.save() sets it before
    // dispatching cms-image-changed) — no page refresh is needed for the
    // widget itself to show the right thumbnail.
    async _writeSlot(imageIds: string[]) {
      try {
        const res = await fetch(`${config.baseURL}/api/content/set-seasonal-image`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({
            locale: config.locale,
            slug: config.areaSlug,
            season: config.slot,
            image_id: imageIds[0] ?? "",
          }),
        });
        if (!res.ok) {
          const errBody = (await res.json().catch(() => ({}))) as { message?: string };
          dispatchToast(errBody.message || config.actionFailedMsg, "error");
          return;
        }
        // A raw fetch() never auto-processes HX-Trigger — re-dispatch it on
        // document.body so the server's success toast fires and
        // HXEventWebsiteContentDraftSaved arms Publish + refreshes the
        // preview, exactly like every other content-overlay write.
        processResponseTriggers(res);
      } catch (err) {
        console.error("[area-envelope-images] write failed", err);
        dispatchToast(config.actionFailedMsg, "error");
      } finally {
        // Release cmsImagePicker's own _saving gate directly (normally
        // cleared by editor.ts's real autosave flush ack, which we never
        // route through) so "remove from pool" isn't left permanently
        // disabled after the first save on this slot. Set on `this` rather
        // than round-tripped through a DOM event, since this method already
        // runs in the SAME merged Alpine scope as the picker it wraps.
        this._saving = false;
      }
    },
  };
}
